import { BaseApiService } from './BaseApiService';

/**
 * Service for handling file uploads
 */
class FileUploadService extends BaseApiService {
  constructor() {
    super();
    // Define common file extension patterns
    this.allowedFilePatterns = {
      documents: /\.(docx|pdf|jpeg)$/i,
      images: /\.(jpeg|jpg|png|gif)$/i,
      all: /\.\w+$/i
    };
    console.log("FileUploadService initialized");
  }

  /**
   * Check if a file has an allowed extension
   * @param {File} file - The file to check
   * @param {string|RegExp} pattern - Pattern to match against
   * @returns {boolean} - Whether the file is allowed
   */
  isFileTypeAllowed(file, pattern = 'all') {
    if (!file || !file.name) return false;
    
    const regexPattern = typeof pattern === 'string' 
      ? this.allowedFilePatterns[pattern] || this.allowedFilePatterns.all
      : pattern;
      
    return regexPattern.test(file.name);
  }

  /**
   * Upload a file with comprehensive logging
   */
  uploadFile(file, fieldId, sectionId, sectionRepeatIndex, formId, userId, onProgress) {
    console.log("Original parameters received:", {
        fieldId,
        sectionId,
        sectionRepeatIndex,
        formId,
        userId
    });
    
    // Add this check
    if (userId !== userId.toString()) {
        console.warn("userId type mismatch - received:", typeof userId, "value:", userId);
    }
    
    console.log("Uploading file with field ID:", fieldId);
    console.log("Uploading file with section ID:", sectionId);
    console.log("Uploading file with section repeat index:", sectionRepeatIndex);
    console.log("Uploading file with form ID:", formId);
    console.log("Uploading file with user ID:", userId);
    // Special logging for field ID 48
    if (fieldId === 48) {
      console.log("UPLOADING FOR ECH PLAN FIELD (ID 48)");
    }
    
    console.log("uploadFile called with params:", { 
      fileName: file?.name, 
      fieldId, 
      sectionId, 
      sectionRepeatIndex, 
      formId, 
      userId 
    });
    
    return new Promise((resolve, reject) => {
      if (!file) {
        console.error("No file provided to uploadFile");
        reject(new Error("No file provided"));
        return;
      }
      
      console.log("File details:", {
        name: file.name,
        type: file.type,
        size: `${(file.size / 1024).toFixed(2)} KB`
      });
      
      const token = localStorage.getItem('token');
      console.log("Auth token from localStorage:", token ? "Present (length: " + token.length + ")" : "Not found");

      console.log("Creating FormData for file upload");
      // Create FormData with detailed logging
      const formData = new FormData();
      
      // Change 'file' to 'value' as required by the API
      formData.append('value', file);
      formData.append('field_id', fieldId.toString());
      formData.append('section', sectionId.toString());
      formData.append('section_repeat_index', sectionRepeatIndex.toString());
      formData.append('form_id', formId.toString());
      formData.append('user_id', userId.toString());
      
      console.log("FormData created with key/values:", {
        'value': file.name,
        'field_id': fieldId.toString(),
        'section': sectionId.toString(),
        'section_repeat_index': sectionRepeatIndex.toString(),
        'form_id': formId.toString(),
        'user_id': userId.toString()
      });
      
      // Check FormData content to ensure it was created correctly
      let formDataLog = "FormData entries: ";
      try {
        for (let pair of formData.entries()) {
          formDataLog += pair[0]+ '=' + (pair[0] === 'value' ? pair[1].name : pair[1]) + ', ';
        }
        console.log(formDataLog);
      } catch (e) {
        console.error("Error logging FormData:", e);
      }
      
      console.log("Setting up config for axios request");
      // Setup axios config
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable && typeof onProgress === 'function') {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            console.log(`Upload progress: ${progress}%`);
            onProgress(progress);
          }
        }
      };
      
      // Add auth token if available
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
        console.log("Added Authorization header with token");
      }
      
      console.log("About to send POST request to /v1/save-file endpoint");
      // Perform the API call with detailed logging
      this.api.post('/v1/save-file', formData, config)
        .then(response => {
          console.log("Upload API call succeeded with status:", response.status);
          console.log("Response headers:", response.headers);
          console.log("Full response data:", response.data);
          
          // Return enhanced response
          const enhancedResponse = {
            ...response.data,
            name: file.name,
            size: file.size,
            type: file.type,
            submission_timestamp: new Date().toISOString(),
            file_id: response.data.submission_id || Date.now(),
            file_url: response.data.file_url || null,
            upload_status: 'server_success'
          };
          
          console.log("Enhanced response with file metadata:", enhancedResponse);
          resolve(enhancedResponse);
        })
        .catch(error => {
          console.error("===== API UPLOAD ERROR =====");
          console.error("Error in file upload:", error.message);
          
          // Create a fallback object
          const fallbackResponse = {
            success: true,
            file_id: Date.now(),
            name: file.name,
            size: file.size,
            type: file.type,
            local: true,
            error: error.message || "Unknown error occurred"
          };
          
          // Log error details
          if (error.response) {
            console.error("Server error response:", error.response.data);
            console.error("Status code:", error.response.status);
            console.error("Headers:", error.response.headers);
          } else if (error.request) {
            console.error("No response received from server. Request:", error.request);
          } else {
            console.error("Error setting up request:", error.message);
          }
          
          console.log("Using fallback response:", fallbackResponse);
          resolve(fallbackResponse);
        });
    });
  }

  /**
   * Try direct upload with axios instance
   */
  uploadFileWithDirectFetch(file, fieldId, sectionId, sectionRepeatIndex, formId, userId) {
    console.log("Trying direct upload with axios instance");
    
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('field_id', fieldId.toString());
      formData.append('section', sectionId.toString());
      formData.append('section_repeat_index', sectionRepeatIndex.toString());
      formData.append('form_id', formId.toString());
      formData.append('user_id', userId.toString());
      
      console.log("Direct upload params:", {
        field_id: fieldId.toString(),
        section: sectionId.toString(),
        section_repeat_index: sectionRepeatIndex.toString(),
        form_id: formId.toString(),
        user_id: userId.toString()
      });
      
      this.api.post('/v1/save-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        }
      })
      .then(response => {
        console.log("Direct upload response:", response.data);
        resolve(response.data);
      })
      .catch(error => {
        console.error('Direct upload error:', error);
        reject(error);
      });
    });
  }
  
  /**
   * Get direct file download link
   * @param {string} submissionId - The submission ID from API response
   * @param {string} fileName - The file name from API response
   * @returns {string} - Complete file URL for download
   */
  getFileUrl(submissionId, fileName) {
    if (!submissionId) return null;
    
    // Construct URL based on submission ID and file name
    return `/files/${submissionId}/${fileName}`;
  }

  /**
   * Compare Postman and app parameters
   */
  compareWithPostman(appParams) {
    console.log("Comparing with successful Postman parameters:");
    console.log("App parameters:", appParams);
    console.log("Expected Postman parameters: form_id:1, user_id:6, field_id:2, section:1, section_repeat_index:0");
    
    
    // Compare and log differences
    for (const key in postmanParams) {
      if (appParams[key] !== postmanParams[key]) {
        console.warn(`Difference in ${key}: App has "${appParams[key]}", Postman has "${postmanParams[key]}"`);
      }
    }
  }

  /**
   * Get file URL by submission ID
   */
  getFileUrlBySubmissionId(submissionId) {
    return new Promise((resolve, reject) => {
      if (!submissionId) {
        resolve(null);
        return;
      }
      
      console.log("Fetching file URL for submission ID:", submissionId);
      
      this.api.get(`/v1/get-file-url/${submissionId}`)
        .then(response => {
          console.log("File URL response:", response.data);
          resolve(response.data.file_url);
        })
        .catch(error => {
          console.error("Error getting file URL:", error);
          resolve(null);
        });
    });
  }
}

// Create and export the service instance
export const fileUploadService = new FileUploadService();

// Add a debug method to the exported instance
fileUploadService.debugToken = function() {
  const token = localStorage.getItem('token');
  console.log("Auth token:", token ? "Present (first 10 chars: " + token.substring(0, 10) + "...)" : "Not found");
  return !!token;
};

export default fileUploadService; 